import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { normalizeApiError } from "../../../lib/result-codes";
import { getCategories } from "../api/category.api";
import type { Category, CategorySortDirection, CategorySortKey } from "../types/category.types";

const PAGE_SIZE = 20;

export function useGetCategoryList() {
  const { run, loading } = useLatestRequest();
  const [items, setItems] = useState<Category[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [type, setType] = useState("");
  const [sortKey, setSortKey] = useState<CategorySortKey | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<CategorySortDirection | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setError(null);
    try {
      const response = await run((signal) =>
        getCategories(
          {
            index: page - 1,
            size: PAGE_SIZE,
            keyword: keyword.trim() || undefined,
            type: type.trim() || undefined,
            sort_key: sortKey,
            sort_direction: sortDirection,
          },
          signal,
        ),
      );
      if (response === undefined) return;
      setItems([...response.list]);
      setTotal(response.paging.total);
    } catch (requestError: unknown) {
      const normalized = normalizeApiError(requestError);
      setError(normalized.user_message || normalized.message || "Failed to load categories");
    }
  }, [keyword, page, run, sortDirection, sortKey, type]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const changeKeyword = useCallback((value: string) => {
    setKeyword(value);
    setPage(1);
  }, []);

  const changeType = useCallback((value: string) => {
    setType(value);
    setPage(1);
  }, []);

  const changeSort = useCallback(
    (key: CategorySortKey, direction: CategorySortDirection | null) => {
      setSortKey(direction ? key : undefined);
      setSortDirection(direction ?? undefined);
      setPage(1);
    },
    [],
  );

  return {
    items,
    page,
    total,
    pageSize: PAGE_SIZE,
    keyword,
    type,
    sortKey,
    sortDirection,
    isLoading: loading,
    error,
    setPage,
    setKeyword: changeKeyword,
    setType: changeType,
    setSort: changeSort,
    refetch,
  };
}
